import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Alert,
  SelectChangeEvent,
  CircularProgress
} from '@mui/material';
import axios from '../../utils/axiosConfig';
import { toast } from '../../utils/toast';

import { Tabs, Tab } from '@mui/material';
import MailConfigTab from '../../components/MailConfigTab';

interface User {
  _id: string;
  name: string;
  email: string;
  role: 'user' | 'admin' | 'organizer' | 'helper';
  isApproved: boolean;
  club?: {
    _id: string;
    name: string;
  };
}

interface UserEditDialogProps {
  open: boolean;
  onClose: () => void;
  user: User | null;
  onUserUpdated: (user: User) => void;
}

const UserEditDialog: React.FC<UserEditDialogProps> = ({
  open,
  onClose,
  user,
  onUserUpdated
}) => {
  const [formData, setFormData] = useState<{
    name: string;
    email: string;
    role: 'user' | 'admin' | 'organizer' | 'helper';
  }>({
    name: '',
    email: '',
    role: 'user'
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState(0);
  const [message, setMessage] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Lade Benutzerdaten, wenn der Dialog geöffnet wird
  useEffect(() => {
    if (user && open) {
      setFormData({
        name: user.name,
        email: user.email,
        role: user.role
      });
      setError(null);
    }
  }, [user, open]);

  // Handler für TextField-Komponenten
  const handleTextFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handler für Select-Komponente
  const handleRoleChange = (e: SelectChangeEvent) => {
    const value = e.target.value as 'user' | 'admin' | 'organizer' | 'helper';
    setFormData(prev => ({
      ...prev,
      role: value
    }));
  };

  const handleSubmit = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // API-Aufruf zum Aktualisieren des Benutzers
      const response = await axios.put(`/api/admin/users/${user._id}`, formData);
      
      // Aktualisiere den Benutzer mit den zurückgegebenen Daten
      onUserUpdated({
        ...user,
        name: formData.name,
        email: formData.email,
        role: formData.role
      });
      
      onClose();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Fehler beim Speichern des Benutzers.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Benutzer bearbeiten
      </DialogTitle>
      <DialogContent>
        {formData.role === 'organizer' && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
              <Tab label="Benutzer" />
              <Tab label="Mail-Config" />
            </Tabs>
          </Box>
        )}
        {message && <Alert severity="success">{message}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}

        {/* Bestehender Content in Tab 0 wrappen */}
        {(formData.role !== 'organizer' || activeTab === 0) && (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              name="name"
              label="Name"
              value={formData.name}
              onChange={handleTextFieldChange}
              fullWidth
              required
            />

            <TextField
              name="email"
              label="E-Mail"
              type="email"
              value={formData.email}
              onChange={handleTextFieldChange}
              fullWidth
              required
            />

            <FormControl fullWidth>
              <InputLabel id="role-select-label">Rolle</InputLabel>
              <Select
                labelId="role-select-label"
                id="role-select"
                name="role"
                value={formData.role}
                label="Rolle"
                onChange={handleRoleChange}
              >
                <MenuItem value="user">Benutzer</MenuItem>
                <MenuItem value="organizer">Organisator</MenuItem>
                <MenuItem value="helper">Orga-Helfer</MenuItem>
                <MenuItem value="admin">Administrator</MenuItem>
              </Select>
            </FormControl>

            {user && user.club && (
              <Paper elevation={0} sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="subtitle1">Zugeordneter Verein</Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  {user.club.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                  Die Vereinszuordnung kann nicht hier bearbeitet werden.
                </Typography>
              </Paper>
            )}

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Passwort</Typography>
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Das Passwort kann derzeit nicht direkt geändert werden.
                </Typography>
                <Button 
                  variant="outlined" 
                  color="warning" 
                  size="small" 
                  sx={{ mt: 1 }} 
                  disabled
                >
                  Passwort zurücksetzen
                </Button>
              </Paper>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        )}
  
        {/* Mail-Config Tab */}
        {formData.role === 'organizer' && activeTab === 1 && user && (
          <MailConfigTab 
            organizerId={user._id}
            onMessage={setMessage}
            onError={setError}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Abbrechen
        </Button>
        <Button 
          onClick={handleSubmit} 
          color="primary" 
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : undefined}
        >
          {loading ? 'Wird gespeichert...' : 'Speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserEditDialog;